<template>
  <div>
            <div class="item-main">
              <v-avatar size="56" rounded class="item-image mr-3">
                <v-img v-if="row.image" :src="row.image" contain />
                <v-icon v-else>mdi-package-variant-closed</v-icon>
              </v-avatar>
              <div class="item-text">
                <div class="font-weight-medium">{{ row.name }}</div>
                <div class="text-caption grey--text">
                  <span v-if="row.sku">{{ row.sku }} · </span>{{ row.quantity }} shipped
                  <span v-if="row.expected_quantity"> · <strong class="primary--text">{{ row.expected_quantity }} expected back</strong></span>
                  <span v-if="row.returned_quantity"> · {{ row.returned_quantity }} already returned</span>
                  <span v-if="row.refunded_quantity"> · {{ row.refunded_quantity }} refunded</span>
                </div>
              </div>
              <div class="quantity-stepper" v-if="row.max_quantity">
                <v-btn icon small @click="step(row, -1)" :disabled="row.receive_quantity <= 0"><v-icon>mdi-minus</v-icon></v-btn>
                <span class="quantity-value">{{ row.receive_quantity }}</span>
                <v-btn icon small @click="step(row, 1)" :disabled="row.receive_quantity >= row.max_quantity"><v-icon>mdi-plus</v-icon></v-btn>
              </div>
              <v-chip v-else small label class="ml-2">Not returnable</v-chip>
            </div>

            <v-expand-transition>
              <div v-if="row.receive_quantity" class="item-condition">
                <v-btn-toggle v-model="row.condition" mandatory rounded dense class="condition-toggle">
                  <v-btn value="good" small :color="row.condition === 'good' ? 'success' : ''" :dark="row.condition === 'good'">
                    <v-icon left small>mdi-check</v-icon>Good
                  </v-btn>
                  <v-btn value="damaged" small :color="row.condition === 'damaged' ? 'error' : ''" :dark="row.condition === 'damaged'">
                    <v-icon left small>mdi-package-variant-remove</v-icon>Damaged
                  </v-btn>
                  <v-btn v-if="allow_hold" value="hold" small :color="row.condition === 'hold' ? 'warning' : ''" :dark="row.condition === 'hold'">
                    <v-icon left small>mdi-magnify-scan</v-icon>Hold
                  </v-btn>
                  <v-btn value="missing" small :color="row.condition === 'missing' ? 'grey darken-1' : ''" :dark="row.condition === 'missing'">
                    <v-icon left small>mdi-help</v-icon>Missing
                  </v-btn>
                </v-btn-toggle>
                <div class="condition-hint text-caption grey--text mt-1">{{ condition_hint(row.condition) }}</div>

                <div v-if="row.condition !== 'good'" class="mt-2">
                  <v-text-field
                    v-model="row.note"
                    dense
                    outlined
                    hide-details
                    counter="255"
                    maxlength="255"
                    :label="row.condition === 'missing' ? 'Note (optional)' : 'What is wrong? (shown to the store owner)'"
                  />
                  <div v-if="photos_enabled && row.condition !== 'missing'" class="photo-row mt-2">
                    <v-btn small outlined rounded @click="pick_photo(row)">
                      <v-icon left small>mdi-camera</v-icon>
                      Add photo{{ photos_required ? ' (required)' : '' }}
                    </v-btn>
                    <v-chip
                      v-for="(photo, i) in row.photos"
                      :key="i"
                      small
                      close
                      class="ml-2"
                      @click:close="row.photos.splice(i, 1)"
                    >
                      <v-icon left small>mdi-image</v-icon>Photo {{ i + 1 }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </v-expand-transition>
  </div>
</template>

<script>
// One line of the register dialog: item, quantity stepper, condition and notes.
// Mutates the row object it is given, like the dialog did before it was split out.
const CONDITION_HINTS = {
  good: 'Restocked in Shopify and put back on its shelf.',
  damaged: 'Not restocked. Reported as damaged on the return so the owner can decide on the refund.',
  hold: 'Set aside without reporting to Shopify. Decide later from the Returns page.',
  missing: 'Expected but not in the parcel. Reported as missing on the return.',
}

export default {
  props: {
    row: { type: Object, required: true },
    allow_hold: { type: Boolean, default: true },
    photos_enabled: { type: Boolean, default: true },
    photos_required: { type: Boolean, default: false },
  },
  methods: {
    condition_hint(condition){
      return CONDITION_HINTS[condition]
    },
    step(row, delta){
      this.$emit('step', row, delta)
    },
    pick_photo(row){
      this.$emit('pick_photo', row)
    },
  },
}
</script>

<style lang="scss" scoped>
.item-main {
  display: flex;
  align-items: center;
}

.item-image {
  background-color: rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
}

.item-text {
  flex: 1;
  min-width: 0;
}

.quantity-stepper {
  display: flex;
  align-items: center;
  margin-left: 8px;
  flex-shrink: 0;
}

.quantity-value {
  min-width: 2em;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
}

.item-condition {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed rgba(0, 0, 0, 0.08);
}

.condition-toggle {
  flex-wrap: wrap;
}

.photo-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
